import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Plus, Loader2, Sparkles, X, Edit2, BookOpen, ChevronDown, Lock, CheckCircle2, Copy, Check, Import } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AudioButton } from "@/components/AudioButton";
import { PageTip } from "@/components/PageTip";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { translateText, categorizeWord } from "@/hooks/useDirectApi";
import { addWordWithGrammar, getStudySets, createStudySet, addWordToStudySet, getOrAnalyzeGrammar, getCategories } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { SupportedLanguage, TranslationSource } from "@/types";
import { parseSentence, ParseSentenceResult } from "@/hooks/useDirectApi";
import { cn } from "@/lib/utils";
import { ImportDialog } from "@/components/ImportDialog";

// TODO: Russian support hidden — re-enable by adding "ru" to InputLanguage and LANGUAGE_LABELS
type InputLanguage = "en" | "ru" | "he";
type TranslationMode = "google" | "openai";

const LANGUAGE_LABELS: Record<InputLanguage, string> = {
  en: "English",
  ru: "Russian", // kept for type safety, not shown in UI
  he: "Hebrew",
};

// TODO: Russian support hidden — re-enable by restoring "he-ru" and "ru-he" cases
function parseDirection(dir: string): { inputLang: InputLanguage; outputLang: "en" | "ru" } {
  switch (dir) {
    case "en-he": return { inputLang: "en", outputLang: "en" };
    case "he-ru": // fall through to default (existing users with ru pref)
    case "ru-he": // fall through to default
    default:      return { inputLang: "he", outputLang: "en" };
  }
}

export function TranslatePage() {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { isPro, canUseFeature, openUpgradePrompt } = useSubscription();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [banner, setBanner] = useState<string | null>(null);

  // Show banner for checkout success or already-pro redirect
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      setBanner("Welcome to Pro! All features are now unlocked.");
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("already_pro") === "1") {
      setBanner("You already have a Pro subscription.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-dismiss banner
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(timer);
  }, [banner]);

  // Mode: "lookup" or "parse"
  const [mode, setMode] = useState<"lookup" | "parse">("lookup");
  const [showImportDialog, setShowImportDialog] = useState(false);

  const defaultDir = parseDirection(preferences.defaultTranslationDirection);
  const [inputText, setInputText] = useState("");
  const [inputLang, setInputLang] = useState<InputLanguage>(defaultDir.inputLang);
  const [outputLang, setOutputLang] = useState<"en" | "ru">(defaultDir.outputLang);
  const [translationMode, setTranslationMode] = useState<TranslationMode>("google");
  const [addresseeGender, setAddresseeGender] = useState<"male" | "female">("male");
  const [addresseeNumber, setAddresseeNumber] = useState<"single" | "plural">("single");

  // Sync with preference changes (e.g. user changes default in dropdown while on this page)
  useEffect(() => {
    const dir = parseDirection(preferences.defaultTranslationDirection);
    setInputLang(dir.inputLang);
    setOutputLang(dir.outputLang);
  }, [preferences.defaultTranslationDirection]);
  const [translatedText, setTranslatedText] = useState("");
  const [transliteration, setTransliteration] = useState<string | null>(null);
  const [editedTranslation, setEditedTranslation] = useState("");
  const [isEditingTranslation, setIsEditingTranslation] = useState(false);
  const [translationSource, setTranslationSource] = useState<TranslationSource | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [isAddedToBank, setIsAddedToBank] = useState(false);
  const [analysisStep, setAnalysisStep] = useState<"idle" | "analyzing" | "saving">("idle");
  const [selectedStudySetId, setSelectedStudySetId] = useState<string | null>(null);
  const [showCreateStudySetDialog, setShowCreateStudySetDialog] = useState(false);
  const [newStudySetName, setNewStudySetName] = useState("");
  const [copiedField, setCopiedField] = useState<"hebrew" | "translation" | null>(null);

  const copyToClipboard = async (text: string, field: "hebrew" | "translation") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
    }
  };

  // Fetch existing categories for smart categorization
  const { data: existingCategories } = useQuery({
    queryKey: ["categories", user?.uid],
    queryFn: () => getCategories(user!.uid),
    enabled: !!user,
  });

  // Fetch study sets
  const { data: studySets } = useQuery({
    queryKey: ["studySets", user?.uid],
    queryFn: () => getStudySets(user!.uid),
    enabled: !!user,
  });

  const selectedStudySet = studySets?.find((s) => s.id === selectedStudySetId);

  // Create study set mutation
  const createStudySetMutation = useMutation({
    mutationFn: () => createStudySet(user!.uid, newStudySetName),
    onSuccess: (newSet) => {
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      setSelectedStudySetId(newSet.id);
      setNewStudySetName("");
      setShowCreateStudySetDialog(false);
    },
  });

  // Determine target language
  const targetLang: SupportedLanguage = inputLang === "he" ? outputLang : "he";

  // Whether to show gender toggle (AI mode + translating TO Hebrew)
  const showGenderToggle = translationMode === "openai" && targetLang === "he";

  // Translation mutation
  const translateMutation = useMutation({
    mutationFn: async () => {
      const result = await translateText(
        inputText.trim(),
        inputLang,
        targetLang,
        translationMode,
        showGenderToggle ? preferences.gender : undefined,
        showGenderToggle ? addresseeGender : undefined,
        showGenderToggle ? addresseeNumber : undefined
      );
      return result;
    },
    onSuccess: async (result) => {
      setTranslatedText(result.translatedText);
      setEditedTranslation(result.translatedText);
      setTranslationSource(result.source);
      setTransliteration(result.transliteration || null);
      setIsAddedToBank(false);
      setIsEditingTranslation(false);

      // Auto-categorize if enabled (Pro only — silently skip for free)
      if (preferences.autoCategories && isPro) {
        const hebrewText = inputLang === "he" ? inputText.trim() : result.translatedText;
        try {
          const categoryNames = existingCategories?.map((c) => c.name) || [];
          const userContext = preferences.onboardingPrompt || "";
          const englishTranslation = inputLang === "he" ? result.translatedText : inputText.trim();
          const suggestedCategories = await categorizeWord(hebrewText, categoryNames, userContext, englishTranslation);
          setCategories(suggestedCategories);
        } catch (error) {
          console.error("Categorization failed:", error);
          setCategories(["Uncategorized"]);
        }
      }
    },
  });

  // Add to word bank mutation
  const addWordMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      const hebrewText = inputLang === "he" ? inputText.trim() : translatedText;
      const translation = inputLang === "he" ? editedTranslation : inputText.trim();
      const translationLang = inputLang === "he" ? targetLang : inputLang;

      // Step 1: Get grammar (Pro: auto-analyze; Free: skip, user can analyze from WordDetailPage)
      let grammar;
      if (isPro) {
        setAnalysisStep("analyzing");
        try {
          grammar = await getOrAnalyzeGrammar(hebrewText);
        } catch {
          // Analysis failed — save word without grammar
          grammar = undefined;
        }
      }

      // Step 2: Save word with grammar included
      setAnalysisStep("saving");
      const wordId = await addWordWithGrammar(
        user.uid,
        hebrewText,
        translation,
        translationLang,
        categories,
        grammar,
        transliteration || undefined
      );

      // Add to study set if selected
      if (selectedStudySetId) {
        await addWordToStudySet(user.uid, selectedStudySetId, wordId);
      }

      setAnalysisStep("idle");
      return wordId;
    },
    onSuccess: () => {
      setIsAddedToBank(true);
      // Invalidate queries so other pages show the new word
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      queryClient.invalidateQueries({ queryKey: ["studySetWords"] });
    },
  });

  const handleTranslate = () => {
    if (!inputText.trim()) return;
    translateMutation.mutate();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTranslate();
    }
  };

  // TODO: Russian support hidden — re-enable getAvailableTargetLangs, handleSourceLangChange, handleTargetLangChange (see git history)

  const clearResults = () => {
    setTranslatedText("");
    setTransliteration(null);
    setEditedTranslation("");
    setCategories([]);
    setIsAddedToBank(false);
  };

  const handleSwap = () => {
    const currentSource = inputLang;
    const currentTarget: InputLanguage = inputLang === "he" ? outputLang : "he";

    // Swap languages
    setInputLang(currentTarget);
    if (currentSource === "he") {
      setOutputLang(currentTarget as "en" | "ru");
    } else {
      setOutputLang(currentSource as "en" | "ru");
    }

    // Swap text if there's a translation
    if (translatedText) {
      const newInputText = translatedText;
      setInputText(newInputText);
      setTranslatedText(inputText.trim());
      setEditedTranslation(inputText.trim());
      // Keep transliteration and other state
    } else {
      clearResults();
    }
  };

  const removeCategory = (categoryToRemove: string) => {
    setCategories(categories.filter((c) => c !== categoryToRemove));
  };

  const addCategory = (newCategory: string) => {
    if (newCategory && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
    }
  };

  const clearAll = () => {
    setInputText("");
    setTranslatedText("");
    setTransliteration(null);
    setEditedTranslation("");
    setCategories([]);
    setIsAddedToBank(false);
    setTranslationSource(null);
    setIsEditingTranslation(false);
    setSelectedStudySetId(null);
  };

  const hebrewText = inputLang === "he" ? inputText.trim() : translatedText;

  return (
    <div className="space-y-6">
      {banner && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {banner}
          <button onClick={() => setBanner(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold">Add Words</h1>
        <p className="text-muted-foreground">
          Enter a word or phrase to translate
        </p>
      </div>
      <PageTip pageKey="add-words" message="Type any Hebrew or English word, hit Translate, then save it to your word bank. Every word gets grammar, categories, and example sentences automatically." />

      {/* Mode Toggle */}
      <div className="flex gap-2" data-walkthrough="translate-mode-toggle">
        <Button
          variant={mode === "lookup" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("lookup")}
        >
          Translate
        </Button>
        <Button
          variant={mode === "parse" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("parse")}
        >
          Chop It Up
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowImportDialog(true)}
          className="gap-1.5"
        >
          <Import className="h-4 w-4" />
          Import File
        </Button>
      </div>

      <ImportDialog open={showImportDialog} onOpenChange={setShowImportDialog} />

      {mode === "parse" ? (
        <ParseSentenceSection />
      ) : (
      <>
      {/* Input Section */}
      <Card data-walkthrough="translate-card">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Language Selectors */}
            <div className="flex flex-wrap items-center gap-2">
              {/* TODO: Russian support hidden — re-enable by restoring language dropdowns (see git history) */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="pointer-events-none">
                  {LANGUAGE_LABELS[inputLang]}
                </Button>

                {/* Swap Button */}
                <Button variant="ghost" size="icon" onClick={handleSwap} className="h-8 w-8" data-walkthrough="translate-swap">
                  <ArrowRightLeft className="h-4 w-4" />
                </Button>

                <Button variant="outline" size="sm" className="pointer-events-none">
                  {LANGUAGE_LABELS[targetLang]}
                </Button>
              </div>

              {/* Translation Mode Toggle */}
              <div className="flex items-center gap-1 ml-auto" data-walkthrough="translate-engine">
                <Button
                  variant={translationMode === "google" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTranslationMode("google")}
                  className="text-xs h-7"
                >
                  Google
                </Button>
                <Button
                  variant={translationMode === "openai" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (!canUseFeature("llmTranslate")) {
                      openUpgradePrompt("llmTranslate");
                      return;
                    }
                    setTranslationMode("openai");
                  }}
                  className="text-xs h-7 gap-1"
                >
                  {isPro ? (
                    <Sparkles className="h-3 w-3" />
                  ) : (
                    <Lock className="h-3 w-3" />
                  )}
                  AI
                </Button>
              </div>
            </div>

            {/* Addressee Gender/Number Toggle - only for AI mode + Hebrew target */}
            {showGenderToggle && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Speaking to:</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant={addresseeGender === "male" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAddresseeGender("male")}
                    className="text-xs h-7 px-2"
                  >
                    Him
                  </Button>
                  <Button
                    variant={addresseeGender === "female" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAddresseeGender("female")}
                    className="text-xs h-7 px-2"
                  >
                    Her
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant={addresseeNumber === "single" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAddresseeNumber("single")}
                    className="text-xs h-7 px-2"
                  >
                    Single
                  </Button>
                  <Button
                    variant={addresseeNumber === "plural" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAddresseeNumber("plural")}
                    className="text-xs h-7 px-2"
                  >
                    Plural
                  </Button>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="relative">
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Enter ${LANGUAGE_LABELS[inputLang].toLowerCase()} word or phrase...`}
                className={cn(
                  "text-lg h-12 pr-10",
                  inputLang === "he" && "hebrew-text"
                )}
                dir={inputLang === "he" ? "rtl" : "ltr"}
              />
              {inputText && (
                <button
                  onClick={clearAll}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Translate Button */}
            <Button
              onClick={handleTranslate}
              disabled={!inputText.trim() || translateMutation.isPending}
              className="w-full"
            >
              {translateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Translating...
                </>
              ) : (
                "Translate"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Translation Result */}
      {translatedText && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Hebrew Display */}
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Hebrew</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-semibold hebrew-text" dir="rtl">
                  {hebrewText}
                </div>
                {hebrewText && <AudioButton text={hebrewText} />}
                {hebrewText && (
                  <button
                    onClick={() => copyToClipboard(hebrewText, "hebrew")}
                    title={copiedField === "hebrew" ? "Copied!" : "Copy to clipboard"}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                  >
                    {copiedField === "hebrew" ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
              {transliteration && (
                <div className="text-base text-muted-foreground italic">
                  {transliteration}
                </div>
              )}
            </div>

            {/* Translation Display (Editable) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{inputLang === "he" ? LANGUAGE_LABELS[outputLang] : LANGUAGE_LABELS[inputLang]}</span>
                  {translationSource && (
                    <Badge variant="secondary" className="text-xs">
                      {translationSource === "openai" && <Sparkles className="h-3 w-3 mr-1" />}
                      {translationSource === "google" ? "Google" : "AI"}
                    </Badge>
                  )}
                  {editedTranslation !== translatedText && (
                    <Badge variant="outline" className="text-xs">
                      Edited
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {editedTranslation && (
                    <button
                      onClick={() => copyToClipboard(editedTranslation, "translation")}
                      title={copiedField === "translation" ? "Copied!" : "Copy to clipboard"}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                    >
                      {copiedField === "translation" ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                  {inputLang === "he" && !isEditingTranslation && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingTranslation(true)}
                      className="h-6 px-2"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              {isEditingTranslation ? (
                <Input
                  value={editedTranslation}
                  onChange={(e) => setEditedTranslation(e.target.value)}
                  onBlur={() => setIsEditingTranslation(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Escape") {
                      setIsEditingTranslation(false);
                    }
                  }}
                  className="text-xl h-10"
                  autoFocus
                />
              ) : (
                <div
                  className="text-xl cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2"
                  onClick={() => inputLang === "he" && setIsEditingTranslation(true)}
                >
                  {inputLang === "he" ? editedTranslation : inputText.trim()}
                </div>
              )}
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Categories</div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Badge
                    key={category}
                    variant="outline"
                    className="cursor-pointer hover:bg-destructive/10"
                    onClick={() => removeCategory(category)}
                  >
                    {category}
                    <X className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
                <CategoryInput onAdd={addCategory} />
              </div>
            </div>

            {/* Scenario Selection */}
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Add to Scenario (optional)</div>
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-between">
                      {selectedStudySet ? (
                        <span className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4" />
                          {selectedStudySet.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">None selected</span>
                      )}
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => setSelectedStudySetId(null)}>
                      <span className="text-muted-foreground">None</span>
                    </DropdownMenuItem>
                    {studySets && studySets.length > 0 && <DropdownMenuSeparator />}
                    {studySets?.map((set) => (
                      <DropdownMenuItem
                        key={set.id}
                        onClick={() => setSelectedStudySetId(set.id)}
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        {set.name}
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {set.wordIds.length}
                        </Badge>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowCreateStudySetDialog(true)}
                  title="Create new scenario"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Word Bank */}
            <Button
              onClick={() => addWordMutation.mutate()}
              disabled={addWordMutation.isPending || isAddedToBank}
              variant={isAddedToBank ? "secondary" : "default"}
              className="w-full"
            >
              {addWordMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {analysisStep === "analyzing" ? "Analyzing grammar..." : "Saving..."}
                </>
              ) : isAddedToBank ? (
                selectedStudySet
                  ? `Added to Word Bank & "${selectedStudySet.name}"`
                  : "Added to Word Bank"
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add to Word Bank
                  {selectedStudySet && ` & "${selectedStudySet.name}"`}
                </>
              )}
            </Button>

            {addWordMutation.isError && (
              <p className="text-sm text-destructive">
                Failed to add word. Please try again.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateStudySetDialog} onOpenChange={setShowCreateStudySetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Scenario</DialogTitle>
            <DialogDescription>
              Create a scenario to prep vocab for a real-life situation
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newStudySetName}
              onChange={(e) => setNewStudySetName(e.target.value)}
              placeholder="e.g., Ulpan Lesson 5, Body Parts"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newStudySetName.trim()) {
                  createStudySetMutation.mutate();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateStudySetDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createStudySetMutation.mutate()}
              disabled={!newStudySetName.trim() || createStudySetMutation.isPending}
            >
              {createStudySetMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

// ===========================================
// Parse Sentence Section
// ===========================================

function ParseSentenceSection() {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [text, setText] = useState("");
  const [sourceLang, setSourceLang] = useState<"he" | "en">("he");
  const [isParsing, setIsParsing] = useState(false);
  const [result, setResult] = useState<ParseSentenceResult | null>(null);
  const [selectedWords, setSelectedWords] = useState<Set<number>>(new Set());
  const [existingNormalized, setExistingNormalized] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [copiedTranslation, setCopiedTranslation] = useState(false);
  const queryClient = useQueryClient();

  const targetLang = sourceLang === "he" ? "en" : "he";

  const copyTranslation = async () => {
    if (!result?.fullTranslation) return;
    try {
      await navigator.clipboard.writeText(result.fullTranslation);
      setCopiedTranslation(true);
      setTimeout(() => setCopiedTranslation(false), 1500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
    }
  };

  // Load existing words for duplicate detection
  useEffect(() => {
    if (!user) return;
    import("@/hooks/useFirestore").then(({ getWords }) => {
      getWords(user.uid).then((words) => {
        const normalized = new Set(words.map((w) => w.hebrew.normalizedText));
        setExistingNormalized(normalized);
      });
    });
  }, [user]);

  const handleParse = async () => {
    if (!text.trim()) return;
    setIsParsing(true);
    setError(null);
    setResult(null);
    setSelectedWords(new Set());
    setSavedCount(0);

    try {
      const parsed = await parseSentence(text.trim(), sourceLang, targetLang);
      setResult(parsed);
    } catch (err: any) {
      setError(err.message || "Failed to parse sentence");
    }
    setIsParsing(false);
  };

  const toggleWord = (position: number) => {
    // Dismiss the "Added N words" banner as soon as the user picks something new,
    // so the Add button takes over again and they can keep adding.
    if (savedCount > 0) setSavedCount(0);
    setSelectedWords((prev) => {
      const next = new Set(prev);
      if (next.has(position)) {
        next.delete(position);
      } else {
        next.add(position);
      }
      return next;
    });
  };

  const handleSaveSelected = async () => {
    if (!user || !result) return;
    const wordsToSave = result.words.filter((w) => selectedWords.has(w.position));
    if (wordsToSave.length === 0) return;

    setIsSaving(true);
    setSaveProgress(0);

    const { addWordWithGrammar, getOrAnalyzeGrammar, updateWordGrammar } = await import("@/hooks/useFirestore");

    let saved = 0;
    for (let i = 0; i < wordsToSave.length; i++) {
      const w = wordsToSave[i];
      setSaveProgress(i + 1);

      // Backend guarantees `original` is always Hebrew, `translation` is the English gloss,
      // regardless of which direction the user typed.
      const hebrew = w.original.trim();
      const english = w.translation.trim();

      // Guard: never save a word without both a Hebrew form and an English translation.
      // This backstops the server in case a per-word translation still came back empty.
      if (!hebrew || !english) {
        console.warn(`Skipping word "${w.original}" — missing translation (hebrew="${hebrew}", english="${english}")`);
        continue;
      }

      try {
        const wordId = await addWordWithGrammar(user.uid, hebrew, english, "en", []);
        saved += 1;

        // Fire-and-forget grammar analysis for Pro users (matches normal Translate flow).
        // Saving doesn't block on this — the word appears in the bank immediately and
        // grammar populates a moment later.
        if (isPro && wordId) {
          getOrAnalyzeGrammar(hebrew)
            .then((grammar) => {
              if (grammar) {
                return updateWordGrammar(
                  user.uid,
                  wordId,
                  grammar.wordType,
                  grammar.nounInfo,
                  grammar.verbInfo,
                  grammar.adjectiveInfo,
                );
              }
            })
            .catch((err) => console.warn(`Grammar analysis failed for "${hebrew}":`, err));
        }
      } catch (err) {
        console.warn(`Failed to save word "${w.original}":`, err);
      }
    }

    setSavedCount(saved);
    setSelectedWords(new Set());
    setIsSaving(false);
    queryClient.invalidateQueries({ queryKey: ["words"] });

    // Refresh existing words set
    const { getWords } = await import("@/hooks/useFirestore");
    const words = await getWords(user.uid);
    setExistingNormalized(new Set(words.map((w) => w.hebrew.normalizedText)));
  };

  const selectedCount = selectedWords.size;

  return (
    <div className="space-y-4">
      {/* Input */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-4">
            <Button
              variant={sourceLang === "he" ? "default" : "outline"}
              size="sm"
              onClick={() => setSourceLang("he")}
            >
              Hebrew → English
            </Button>
            <Button
              variant={sourceLang === "en" ? "default" : "outline"}
              size="sm"
              onClick={() => setSourceLang("en")}
            >
              English → Hebrew
            </Button>
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={sourceLang === "he"
              ? "Paste a Hebrew message, email, or paragraph..."
              : "Paste an English sentence you want to say in Hebrew..."
            }
            className="w-full min-h-[120px] p-3 border rounded-lg text-base resize-y"
            dir={sourceLang === "he" ? "rtl" : "ltr"}
          />

          <Button
            onClick={handleParse}
            disabled={!text.trim() || isParsing}
            className="w-full"
          >
            {isParsing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Parsing...
              </>
            ) : (
              "Chop It Up"
            )}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive text-center">{error}</p>
      )}

      {/* Results */}
      {result && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Full sentence translation */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-muted-foreground">Full Translation</div>
                <button
                  onClick={copyTranslation}
                  title={copiedTranslation ? "Copied!" : "Copy to clipboard"}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                >
                  {copiedTranslation ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <div className="text-sm" dir={targetLang === "he" ? "rtl" : "ltr"}>
                {result.fullTranslation}
              </div>
            </div>

            {/* Word bubbles (deduplicated) */}
            <div>
              {(() => {
                const seen = new Set<string>();
                const isHebrew = (text: string) => /[\u0590-\u05FF]/.test(text);
                const uniqueWords = result.words.filter((w) => {
                  if (seen.has(w.normalized)) return false;
                  // Words are always Hebrew now — skip any non-Hebrew leftover (e.g. digits)
                  if (!isHebrew(w.original)) return false;
                  seen.add(w.normalized);
                  return true;
                });
                return (
                  <>
                  <div className="text-xs text-muted-foreground mb-2">
                    Tap words you want to learn ({uniqueWords.length} unique words)
                  </div>
                  <div className="flex flex-wrap gap-2">
                {uniqueWords.map((word) => {
                  const hebrewWord = word.original;
                  const normalized = hebrewWord.replace(/[\u0591-\u05C7]/g, "").trim();
                  const alreadySaved = existingNormalized.has(normalized);
                  const noTranslation = !word.translation?.trim();
                  const disabled = alreadySaved || noTranslation;
                  const isSelected = selectedWords.has(word.position);

                  return (
                    <button
                      key={`${word.position}-${word.original}`}
                      onClick={() => !disabled && toggleWord(word.position)}
                      disabled={disabled}
                      title={noTranslation ? "No translation available for this word — can't be added" : undefined}
                      className={cn(
                        "flex flex-col items-center px-3 py-2 rounded-lg border text-sm transition-all",
                        alreadySaved
                          ? "bg-muted/30 border-muted text-muted-foreground opacity-50 cursor-default"
                          : noTranslation
                          ? "bg-muted/20 border-dashed border-muted-foreground/30 text-muted-foreground opacity-60 cursor-not-allowed"
                          : isSelected
                          ? "bg-primary/10 border-primary text-primary shadow-sm"
                          : "bg-background border-border hover:border-primary/50 cursor-pointer"
                      )}
                    >
                      <span className="font-medium hebrew-text" dir="rtl">
                        {word.original}
                      </span>
                      <span className="text-xs text-muted-foreground mt-0.5">
                        {word.translation || "—"}
                      </span>
                      {alreadySaved && (
                        <span className="text-[10px] text-green-600 mt-0.5">✓ saved</span>
                      )}
                      {!alreadySaved && noTranslation && (
                        <span className="text-[10px] text-muted-foreground mt-0.5 italic">no translation</span>
                      )}
                    </button>
                  );
                })}
              </div>
                  </>
                );
              })()}
            </div>

            {/* Save button */}
            {savedCount > 0 && (
              <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Added {savedCount} word{savedCount !== 1 ? "s" : ""} to your word bank!
              </div>
            )}
            <Button
              onClick={handleSaveSelected}
              disabled={selectedCount === 0 || isSaving}
              className="w-full"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding {saveProgress}/{selectedCount}...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add {selectedCount} Word{selectedCount !== 1 ? "s" : ""} to Word Bank
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Mini component for adding new categories
function CategoryInput({ onAdd }: { onAdd: (category: string) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("");

  const handleSubmit = () => {
    if (value.trim()) {
      onAdd(value.trim());
      setValue("");
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
          if (e.key === "Escape") {
            setIsEditing(false);
            setValue("");
          }
        }}
        onBlur={handleSubmit}
        placeholder="New category"
        className="h-6 w-32 text-xs"
        autoFocus
      />
    );
  }

  return (
    <Badge
      variant="outline"
      className="cursor-pointer border-dashed hover:bg-accent"
      onClick={() => setIsEditing(true)}
    >
      <Plus className="h-3 w-3 mr-1" />
      Add
    </Badge>
  );
}
